import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import Keycloak from 'keycloak-js';
import {AuthService} from '../../guard/auth.service';
import {EntitlementsStore} from './entitlements.store';
import {MonAbonnementStore} from './mon-abonnement.store';
import {RealtimeNotificationsService} from './realtime/realtime-notifications.service';

/**
 * Applique un changement de droits sans déconnexion : force le rafraîchissement du token Keycloak
 * (`updateToken(-1)` ⇒ nouveau JWT portant la licence à jour), réinterprète la licence, puis recharge
 * les droits effectifs et l'action d'abonnement. Déclenché par l'événement serveur `droitsMisAJour`
 * (renouvellement, suspension… appliqués depuis Odoo), ou au retour sur l'onglet après la gestion de
 * l'abonnement dans le portail Odoo. Les appels concurrents sont fusionnés.
 */
@Injectable({providedIn: 'root'})
export class DroitsRefreshService {
  private readonly keycloak = inject(Keycloak);
  private readonly auth = inject(AuthService);
  private readonly entitlements = inject(EntitlementsStore);
  private readonly abonnement = inject(MonAbonnementStore);
  private readonly realtime = inject(RealtimeNotificationsService);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  private inFlight: Promise<void> | null = null;

  /** Écoute les changements de droits signalés par le serveur (idempotent). */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.realtime.droitsMisAJour$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.refresh());
  }

  /** Rafraîchit token, licence et droits (appels concurrents fusionnés). */
  refresh(): Promise<void> {
    this.inFlight ??= this.doRefresh().finally(() => (this.inFlight = null));
    return this.inFlight;
  }

  private async doRefresh(): Promise<void> {
    try {
      await this.keycloak.updateToken(-1);
    } catch {
      // Session expirée : rien à rafraîchir, l'intercepteur d'erreur gère la reconnexion au prochain appel.
      return;
    }
    this.auth.interpretLicense(this.keycloak.tokenParsed?.['licence']);
    await Promise.all([this.entitlements.reload(), this.abonnement.reload()]);
  }
}
